import { SheetSyncEvents } from "../SheetSync/SheetSyncConstants.js";
import { getCameraRotationAngle, mapPixelsToWidget, mapPixelsToWorld, widgetToMapPixels, worldToMapPixels } from '../SheetSync/SheetTransforms.js';
import AvatarWidget from './AvatarWidget.js';

const av = Autodesk.Viewing;
const namespace = AutodeskNamespace('Autodesk.AEC');
const myExtensionName = 'Autodesk.AEC.AvatarExtension';

const FOCUS_DURATION = 2.0;

/**
// AvatarExtension - A viewer extension that displays an avatar on a 2D sheet, based on a targetViewer's 3D camera.
// @param {Viewer3D} [viewer]                            - viewer instance
// @param {Object} [options]                             - Configuration options for AvatarExtension
*/
export default class AvatarExtension extends Autodesk.Viewing.Extension {
    constructor(viewer, options) {
        super(viewer, options);

        this.options = this.options.avatarOptions || this.options;

        // Auto focus camera on avatar.
        this.autoFocus = this.options.autoFocus === undefined ? true : this.options.autoFocus;

        // Indicates if the avatar is currently out of focus.
        this.outOfFocus = true;

        const disableAnimations = this.options.disableAnimations ?? false;
        // Time to focus the camera on avatar.
        this.focusDuration = disableAnimations ? 0 : FOCUS_DURATION;

        // Last 3D camera position.
        this.last3dCameraPos = new THREE.Vector3();

        this.update = this.update.bind(this);
        this.onMainMapChanged = this.onMainMapChanged.bind(this);
        this.on3dCameraChanged = this.on3dCameraChanged.bind(this);
        this.onSheetCameraChanged = this.onSheetCameraChanged.bind(this);
        this.onDragStart = this.onDragStart.bind(this);
        this.onDragEnd = this.onDragEnd.bind(this);
    }

    async load() {
        this.sheetSyncExt = await this.viewer.loadExtension('Autodesk.AEC.SheetSyncExtension', this.viewer.config);
        if (!this.sheetSyncExt) {
            return false;
        }

        this.targetViewer = this.sheetSyncExt.targetViewer;

        const transforms = {
            mapPixelsToWorld: this.mapPixelsToWorld.bind(this),
            worldToMapPixels: this.worldToMapPixels.bind(this),
            mapPixelsToWidget: this.mapPixelsToWidget.bind(this),
            widgetToMapPixels: this.widgetToMapPixels.bind(this),
            getMapRotationAngle: this.getCameraRotationAngle.bind(this)
        };

        this.avatarWidget = new AvatarWidget(this.viewer.container, this.targetViewer, this.globalManager, transforms, this.options.trackUsage, this.viewer.canvasWrap);

        this.visible = true;

        if (this.autoFocus) {
            // Set initial center & zoom level according to model bounds.
            const targetPos = this.getFuzzyBoundsCenter();

            // If fuzzyBounds not ready yet - it means that there are no models ready yet in the target viewer.
            // in that case, wait for the first loaded model, and them set the initial zoom.
            if (targetPos) {
                this.setCameraPosition(targetPos, true);
            } else {
                this.targetViewer.addEventListener(av.GEOMETRY_LOADED_EVENT, () => {
                    this.focusOnModel();
                }, { once: true });
            }
        }

        this.addListeners();
        this.activateTool();

        return true;
    }

    unload() {
        this.sheetSyncExt = null;

        this.removeListeners();
        this.deactivateTool();

        this.avatarWidget.destroy();
        this.avatarWidget = null;

        if (this.focusAnim) {
            this.focusAnim.stop();
            this.focusAnim = null;
        }

        return true;
    }

    activateTool() {
        if (this.toolActive) {
            return;
        }

        this.toolActive = true;

        this.viewer.toolController.registerTool(this.avatarWidget.tool);
        this.viewer.toolController.activateTool(this.avatarWidget.tool.getName());

        this.avatarWidget.addEventListener(Autodesk.AEC.AvatarEvents.DRAG_START_EVENT, this.onDragStart);
        this.avatarWidget.addEventListener(Autodesk.AEC.AvatarEvents.DRAG_END_EVENT, this.onDragEnd);

        this.updateFocus();
    }

    deactivateTool() {
        if (!this.toolActive) {
            return;
        }

        this.toolActive = false;

        this.viewer.toolController.deactivateTool(this.avatarWidget.tool.getName());
        this.viewer.toolController.deregisterTool(this.avatarWidget.tool);

        this.avatarWidget.removeEventListener(Autodesk.AEC.AvatarEvents.DRAG_START_EVENT, this.onDragStart);
        this.avatarWidget.removeEventListener(Autodesk.AEC.AvatarEvents.DRAG_END_EVENT, this.onDragEnd);
    }

    addListeners() {
        this.targetViewer.addEventListener(av.NAVIGATION_MODE_CHANGED_EVENT, this.update);
        this.targetViewer.addEventListener(av.MODEL_ADDED_EVENT, this.update); // Whenever a model has changed in this 3D viewer, update the widget accordingly.
        this.targetViewer.addEventListener(av.MODEL_REMOVED_EVENT, this.update); // Whenever a model has changed in this 3D viewer, update the widget accordingly.
        this.targetViewer.addEventListener(av.CAMERA_CHANGE_EVENT, this.on3dCameraChanged); // Whenever the 3D camera moved, update the widget accordingly.
        this.viewer.addEventListener(av.MODEL_ADDED_EVENT, this.update); // Whenever a model has changed in this 2D viewer, update the widget accordingly.
        this.viewer.addEventListener(av.MODEL_REMOVED_EVENT, this.update); // Whenever a model has changed in this 2D viewer, update the widget accordingly.
        this.viewer.addEventListener(av.RENDER_PRESENTED_EVENT, this.update); //  Whenever the 2D camera moved, update the widget accordingly (no viewport change).
        this.viewer.addEventListener(av.VIEWER_RESIZE_EVENT, this.update); //  Whenever the screen size has been changed.
        this.viewer.addEventListener(SheetSyncEvents.MAIN_MAP_CHANGED_EVENT, this.onMainMapChanged);
        this.viewer.addEventListener(av.CAMERA_CHANGE_EVENT, this.onSheetCameraChanged); // Whenever the 2d camera moved.
    }

    removeListeners() {
        this.targetViewer.removeEventListener(av.NAVIGATION_MODE_CHANGED_EVENT, this.update);
        this.targetViewer.removeEventListener(av.MODEL_ADDED_EVENT, this.update);
        this.targetViewer.removeEventListener(av.MODEL_REMOVED_EVENT, this.update);
        this.targetViewer.removeEventListener(av.CAMERA_CHANGE_EVENT, this.on3dCameraChanged);
        this.viewer.removeEventListener(av.MODEL_ADDED_EVENT, this.update);
        this.viewer.removeEventListener(av.MODEL_REMOVED_EVENT, this.update);
        this.viewer.removeEventListener(av.RENDER_PRESENTED_EVENT, this.update);
        this.viewer.removeEventListener(av.VIEWER_RESIZE_EVENT, this.update);
        this.viewer.removeEventListener(SheetSyncEvents.MAIN_MAP_CHANGED_EVENT, this.onMainMapChanged);
        this.viewer.removeEventListener(av.CAMERA_CHANGE_EVENT, this.onSheetCameraChanged); // Whenever the 2d camera moved.
    }

    onDragStart() {
        if (this.focusAnim) {
            this.focusAnim.stop();
            this.focusAnim = null;
        }
    }

    onDragEnd() {
        this.outOfFocus = true;
        this.updateFocus();
    }

    isEnabled() {
        return this.sheetSyncExt.isEnabled();
    }

    onMainMapChanged({ sheetMap }) {
        if (sheetMap) {
            this.update();
        }
    }

    update() {
        if (!this.viewer.impl || !this.viewer.model || !this.avatarWidget || !this.sheetSyncExt) {
            return;
        }

        let promise;

        if (this.sheetSyncExt.sheetMap && this.isEnabled() && this.visible) {
            promise = this.avatarWidget.show();
            this.activateTool();
        } else {
            promise = this.avatarWidget.hide();
            this.deactivateTool();
        }

        // Update only if a sheetMap is loaded, otherwise, there won't be any transforms available.
        if (this.sheetSyncExt.sheetMap) {
            this.avatarWidget.update();
        }

        return promise;
    }

    show() {
        if (this.visible) {
            return;
        }

        this.visible = true;

        // Returns a promise that should be resolved after the fading animation is over.
        return this.update();
    }

    hide() {
        if (!this.visible) {
            return;
        }

        this.visible = false;

        // Returns a promise that should be resolved after the fading animation is over.
        return this.update();
    }

    setCameraPosition(pos, changeZoom) {
        const camera = this.viewer.getCamera();

        // Change x & y values, but keep the same camera distance if changeZoom is off.
        camera.position.set(pos.x, pos.y, changeZoom ? pos.z : camera.position.z);
        camera.target.set(pos.x, pos.y, camera.target.z);

        camera.dirty = true;
    }

    // Map angle is determined according to the viewer's camera rotation.
    getCameraRotationAngle() {
        return getCameraRotationAngle(this.viewer);
    }

    // Transform 3D model position to pixel-space position on full MiniMap. Indepdent of scroll-position.
    worldToMapPixels(pos3D) {
        return worldToMapPixels(this.sheetSyncExt.sheetMap, pos3D);
    }

    // Convert minimap pixel coords (+ world-z) to 3D world position. Minimap pixels are absolute, i.e. not affected by scrolling.
    mapPixelsToWorld(posX, posY, zWorld) {
        return mapPixelsToWorld(this.sheetSyncExt.sheetMap, posX, posY, zWorld);
    }

    mapPixelsToWidget(p) {
        return mapPixelsToWidget(this.sheetSyncExt.sheetMap, this.viewer, p);
    }

    // @param {Vector2} p - transformed in-place from widget pixels to map image pixels
    //                      widget pixel origin is at top-left, i.e., y-axis points down (compatible to mouse events)
    // @param {boolean} clamp - In case the resulting position is outside of the map, clamp the value to fit the maps' border.
    widgetToMapPixels(p, clamp) {
        clamp = clamp && !this.sheetSyncExt.options.cropToViewport;
        return widgetToMapPixels(this.sheetSyncExt.sheetMap, this.viewer, p, clamp);
    }

    world3DtoWorld2D(point) {
        const pos = this.worldToMapPixels(point);
        this.mapPixelsToWidget(pos);
        const worldPos = this.viewer.clientToWorld(pos.x, pos.y, undefined, true).point;

        return worldPos;
    }

    // Find avatar position according to current 3D camera location.
    getAvatarPositionOn2DSheet() {
        return this.world3DtoWorld2D(this.targetViewer.getCamera().position);
    }

    on3dCameraChanged() {
        this.updateFocus();
        this.update();
    }

    onSheetCameraChanged() {
        const navTool = this.viewer.toolController.getTool('pan');
        const isPanning = navTool.isInteractionActive();

        if (isPanning) {
            this.outOfFocus = true;

            // If panning has been made during animation - cancel the animation.
            if (this.focusAnim) {
                this.focusAnim.stop();
                this.focusAnim = null;
            }
        }
    }

    // Zoom 2D camera to avatar position.
    focusOnAvatar() {
        this.animateFocus(false, () => {
            this.outOfFocus = false;
        });
    }

    // Zoom 2D camera to model bounds.
    focusOnModel() {
        this.animateFocus(true, () => {
            this.outOfFocus = true;
        });
    }

    updateFocus() {
        if (!this.sheetSyncExt || !this.sheetSyncExt.sheetMap || !this.autoFocus || this.avatarWidget.tool.isDragging) {
            this.outOfFocus = true;
            return;
        }

        const camera3d = this.targetViewer.getCamera();

        // Focus the 2D camera only if the 3D camera really moved.
        // Really moved <==> changed x or y position (We ignore z by purpose).
        if (this.last3dCameraPos.x === camera3d.position.x && this.last3dCameraPos.y === camera3d.position.y) {
            return;
        }

        if (!this.isAvatarInsideBounds()) {
            this.focusOnModel();
        } else if (this.outOfFocus) {
            // Animate the transition if we are after:
            // - Panning
            // - Manually dragging the avatar
            // - Double clicked to fly to location
            // - Was outside of the model's visible bounds and now got back in
            this.focusOnAvatar();
        } else {
            // Otherwise, the avatar is already focused, and just need to be updated to the last camera movement.
            const pos = this.getAvatarPositionOn2DSheet();
            this.setCameraPosition(pos);
        }

        this.last3dCameraPos = camera3d.position.clone();
    }

    getFuzzyBoundsCenter() {
        const bounds3D = this.sheetSyncExt.getFuzzyBox();

        if (bounds3D.isEmpty()) {
            return null;
        }

        const min = this.world3DtoWorld2D(bounds3D.min);
        const max = this.world3DtoWorld2D(bounds3D.max);
        const bounds2D = new THREE.Box3().setFromPoints([min, max]);

        const camera = this.viewer.getCamera();
        var fit = this.viewer.navigation.computeFit(camera.position, camera.target, camera.fov, bounds2D, camera.aspect);

        return fit.position;
    }

    isAvatarInsideBounds() {
        const bounds = this.sheetSyncExt.getFuzzyBox();

        if (bounds.isEmpty()) {
            return false;
        }

        // Ignore height when checking these bounds. We care only about x & y here.
        bounds.min.z = -Infinity;
        bounds.max.z = Infinity;

        const pos = this.targetViewer.getCamera().position;
        return bounds.containsPoint(pos);
    }

    animateFocus(isOutside, onDone) {
        if (this.focusAnim) {
            return;
        }

        const camera = this.viewer.getCamera();

        const startPos = camera.position;

        const onTimer = (t) => {
            if (!this.toolActive) {
                return;
            }

            const targetPos = isOutside ? this.getFuzzyBoundsCenter() : this.getAvatarPositionOn2DSheet();

            if (targetPos) {
                t = Autodesk.Viewing.Private.smootherStep(t);
                const pos = startPos.clone().lerp(targetPos, t);
                this.setCameraPosition(pos, isOutside);
            }
        };

        const onFadeEnd = () => {
            this.focusAnim = null;
            onDone && onDone();
        };

        const anim = Autodesk.Viewing.Private.fadeValue(0, 1, this.focusDuration, onTimer, onFadeEnd);
        if (anim.isRunning) {
            this.focusAnim = anim;
        }
    }

    getAutoFocus() {
        return this.autoFocus;
    }

    setAutoFocus(enable) {
        this.autoFocus = enable;
        this.updateFocus();
    }

    setDragDirectionLocked(isLocked) {
        this.avatarWidget.tool.isDragDirectionLocked = isLocked;
    }
}

namespace.AvatarExtension = AvatarExtension;
av.theExtensionManager.registerExtension(myExtensionName, AvatarExtension);
